/** WHICH CLIENTS FOLLOW A MODEL — answered the only way the backend allows. */
import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { GET_GOALS } from '../queries';
import { asClient } from '../clientScope';
import { useClients } from './clients';

/** Clients read at once. Enough to be quick, few enough to be polite. */
const BATCH = 8;

/** What the page shows for a model nobody has scanned yet. */
const NOT_SCANNED = Object.freeze({
  running: false, done: false, scanned: 0, rows: Object.freeze([]), error: null,
});

/**
 * The clients whose goals are planned against a given model.
 *
 * There is no resolver for this. `getUserModelPortfolios` takes no arguments —
 * it answers for the caller — and neither the portfolio nor the model-portfolio
 * type carries a client. A goal names its model through its policy portfolio,
 * and `getGoals` answers for ONE client at a time, so the only way to ask the
 * question is to ask it of every client in turn.
 *
 * That is why this does not run on page load. It is a scan the reader starts,
 * it reports progress while it runs, and it can be read before it finishes.
 *
 * `find()` resolves with the scan's outcome, `{ rows, scanned, total, error }`,
 * so a caller that needs the answer (the agent's run) awaits it rather than
 * watching renders. Calling it while a scan of the same model is running joins
 * that scan instead of starting a second one, which would write over the
 * first one's rows. The page stays mounted from one model to the next, so the
 * state belongs to the model it was scanned for: another model reads as not
 * scanned, and a scan that is no longer the current one never writes into it.
 *
 * "Current" is the scan, not the model. Going from model A to B and back to A
 * while A's first scan is still running starts a second scan of A, and a guard
 * on the model id alone let both write into A's state — the first one's finish
 * marked the panel done, and the second one's next batch then rewound its
 * counts under a panel that no longer said it was scanning.
 */
export function useModelFollowers(modelId) {
  const apollo = useApolloClient();
  const clients = useClients();
  const wantedId = String(modelId);
  const [state, setState] = useState({ ...NOT_SCANNED, modelId: wantedId });
  const inFlight = useRef(null);

  const find = useCallback(() => {
    const wanted = String(modelId);
    if (inFlight.current?.modelId === wanted) return inFlight.current.promise;

    const book = clients.rows;
    /* Writes for this scan only land while the state still belongs to this scan.
       Its own `find()` caller still gets its rows through the promise. */
    const token = {};
    const write = (update) => setState((prev) => (prev.scan === token ? { ...prev, ...update } : prev));
    setState({ ...NOT_SCANNED, running: true, modelId: wanted, scan: token });

    const scan = async () => {
      const found = [];
      let scanned = 0;
      try {
        for (let i = 0; i < book.length; i += BATCH) {
          const slice = book.slice(i, i + BATCH);
          /* eslint-disable no-await-in-loop -- batched on purpose: the whole book
             at once is hundreds of parallel requests. */
          const answers = await Promise.all(slice.map(async (c) => {
            try {
              const { data } = await apollo.query({
                query: GET_GOALS,
                context: asClient(c.id),
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
              });
              return { client: c, goals: data?.getGoals?.goals ?? [] };
            } catch {
              /* One client's goals failing is not the scan failing. */
              return { client: c, goals: [] };
            }
          }));
          /* eslint-enable no-await-in-loop */

          answers.forEach(({ client, goals }) => {
            goals.forEach((g) => {
              const linked = g.policyPortfolio?.modelPortfolio?.assetClassModelId
                ?? g.recommendedModel?.assetClassModelId;
              if (linked != null && String(linked) === wanted) {
                found.push({
                  clientId: client.id,
                  clientName: client.name,
                  goalId: g.goalId,
                  goalName: g.displayName || g.goalName,
                  target: g.goalTarget,
                  policyName: g.policyPortfolio?.policyName || g.policyPortfolio?.portfolioName || null,
                  modelName: g.recommendedModel?.modelName || null,
                });
              }
            });
          });

          scanned = Math.min(i + BATCH, book.length);
          write({ scanned, rows: [...found] });
        }
        write({ running: false, done: true });
        return { rows: [...found], scanned, total: book.length, error: null };
      } catch (err) {
        write({ running: false, done: true, error: err.message });
        return { rows: [...found], scanned, total: book.length, error: err.message };
      }
    };

    const promise = scan().finally(() => {
      if (inFlight.current?.promise === promise) inFlight.current = null;
    });
    inFlight.current = { modelId: wanted, promise };
    return promise;
  }, [apollo, clients.rows, modelId]);

  const view = state.modelId === wantedId ? state : NOT_SCANNED;
  return {
    running: view.running,
    done: view.done,
    scanned: view.scanned,
    rows: view.rows,
    error: view.error,
    total: clients.rows.length,
    clientsLoading: clients.loading,
    /* A book that failed to load reads as no clients; this says which it was. */
    clientsError: clients.error || null,
    find,
  };
}
